import type { ReactNode } from 'react';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Link from '@mui/material/Link';
import Paper from '@mui/material/Paper';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import { useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import {
  formatCivilDate,
  formatCurrency,
  formatPeriod,
  addMonths,
  isBillingUnavailable,
  links,
  useEstimate,
  useMasterInvoiceLines,
  useMemberEstimate,
  usePolicyBilling,
  usePolicyInvoices,
} from '@eis/core';
import { DetailsSection, EmptyState, ErrorState, RequirePermission, StatusChip } from '@eis/patterns';
import { insurancePermissions as p } from '../../../permissions';

/**
 * The policy's `billing` tab (SDD-015 §7.2): what the policy owes and when — read from the billing
 * service through `@eis/core`, in the operations persona's words, and never a byte of billing
 * module code (backbone §1).
 *
 * Four answers: the SCHEDULE row (own billing or through the Gruppen-Police, the next invoice
 * date, the next period — B-10); the RUNNING ESTIMATE (B-9); the policy's OWN invoices; and, for a
 * member, the master lines that name it — "billed through the group policy" (B-8, SSOT I-20). The
 * actions send the operator into the billing module's dialogs through its link builders, prefilled
 * with this policy; whoever may not `create` or `correct` there sees no button.
 *
 * An outage is an outage: the policy still renders (the tab is a section of a page the insurance
 * service answered), and this tab says the billing service did not answer — never "no invoices".
 */
export function PolicyBillingTab({
  code,
  locale,
}: {
  readonly code: string;
  readonly locale: string;
}): ReactNode {
  const { t } = useTranslation('insurance');
  const navigate = useNavigate();
  const schedule = usePolicyBilling(code);
  const estimate = useEstimate(code);
  // A member has no estimate of its own (A-52): its line sits on the master's estimate.
  const share = useMemberEstimate(code);
  const own = usePolicyInvoices(code);
  const masterLines = useMasterInvoiceLines(code);

  const queries = [schedule, estimate, share, own, masterLines];
  const failed = queries.find((query) => query.isError);
  if (failed?.isError === true) {
    const down = isBillingUnavailable(failed.error);
    return (
      <ErrorState
        description={down ? t('policies.billing.unavailable') : t('policies.billing.error')}
        details={failed.error.message}
        onRetry={() => {
          for (const query of queries) void query.refetch();
        }}
      />
    );
  }
  if (queries.some((query) => query.isPending)) {
    return <Skeleton variant="rounded" height={320} data-testid="billing-loading" />;
  }

  const row = schedule.data?.policyBilling;
  const viaMaster = row?.status === 'via_master';
  const master = row?.groupPolicyCode ?? '';
  const nextDate = (row?.nextBillingDate ?? '').slice(0, 10);
  const running = estimate.data;
  const invoices = own.data?.items ?? [];
  const lines = masterLines.data ?? [];
  const money = (minor: number | undefined, currency: string | undefined): string =>
    minor === undefined ? '—' : formatCurrency(minor, currency === undefined || currency === '' ? 'EUR' : currency, locale);
  const period = (from: string | undefined, to: string | undefined): string => {
    const start = (from ?? '').slice(0, 10);
    const end = (to ?? '').slice(0, 10);
    if (start === '') return '—';
    if (start.endsWith('-01') && addMonths(start, 1) === end) return formatPeriod(start.slice(0, 7), locale);
    return `${formatCivilDate(start, locale)} – ${end === '' ? '' : formatCivilDate(end, locale)}`;
  };
  const openInvoice = (invoiceCode: string): void => {
    void navigate({ href: links.billing.invoice(invoiceCode) });
  };
  const openBilling = (action: 'issue' | 'period' | 'correction' | 'schedule'): void => {
    void navigate({ href: links.billing.invoices({ policyCode: code, action }) });
  };

  return (
    <Stack spacing={2.5} data-testid="policy-billing">
      <DetailsSection
        title={t('policies.billing.title')}
        description={t('policies.billing.description')}
        fields={[
          {
            label: t('policies.billing.mode'),
            value: viaMaster ? (
              <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                <span>{t('policies.billing.viaMaster')}</span>
                {master !== '' && (
                  <Link
                    component="button"
                    type="button"
                    sx={{ fontFamily: 'monospace' }}
                    onClick={() => {
                      void navigate({ href: links.insurance.groupPolicy(master, 'invoices') });
                    }}
                  >
                    {master}
                  </Link>
                )}
              </Stack>
            ) : row === undefined ? (
              t('policies.billing.notScheduled')
            ) : (
              t('policies.billing.own')
            ),
          },
          {
            label: t('policies.billing.status'),
            value:
              row === undefined ? (
                '—'
              ) : (
                <StatusChip
                  status={row.status ?? ''}
                  label={t(`policies.billing.scheduleStatus.${row.status ?? ''}` as never, { defaultValue: row.status ?? '' })}
                />
              ),
          },
          {
            label: t('policies.billing.nextInvoice'),
            value: nextDate === '' ? '—' : formatCivilDate(nextDate, locale),
          },
          { label: t('policies.billing.nextPeriod'), value: period(row?.nextPeriodFrom, row?.nextPeriodTo) },
          {
            label: t('policies.billing.estimate'),
            value: viaMaster ? (
              share.data === null || share.data === undefined ? (
                t('policies.billing.noEstimate')
              ) : (
                <Stack direction="row" spacing={1} useFlexGap sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
                  <strong data-testid="running-estimate">
                    {money(share.data.line?.newAmount ?? share.data.estimate.grossAmount, share.data.estimate.currency)}
                  </strong>
                  <Typography variant="body2" color="text.secondary">
                    {t('policies.billing.estimateViaMaster', {
                      total: money(share.data.estimate.grossAmount, share.data.estimate.currency),
                      period: period(share.data.estimate.billingIntervalFrom, share.data.estimate.billingIntervalTo),
                      at: (share.data.estimate.estimatedAt ?? '') === '' ? '—' : formatCivilDate((share.data.estimate.estimatedAt ?? '').slice(0, 10), locale),
                    })}
                  </Typography>
                  <Link component="button" type="button" variant="body2" onClick={() => { openInvoice(share.data?.estimate.code ?? ''); }}>
                    {t('policies.billing.estimateBreakdown')}
                  </Link>
                </Stack>
              )
            ) : running === null || running === undefined ? (
              t('policies.billing.noEstimate')
            ) : (
              <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                <strong data-testid="running-estimate">{money(running.grossAmount, running.currency)}</strong>
                <Typography variant="body2" color="text.secondary">
                  {t('policies.billing.estimateFor', {
                    period: period(running.billingIntervalFrom, running.billingIntervalTo),
                    at: (running.estimatedAt ?? '') === '' ? '—' : formatCivilDate((running.estimatedAt ?? '').slice(0, 10), locale),
                  })}
                </Typography>
              </Stack>
            ),
          },
        ]}
        actions={
          <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: 'wrap' }}>
            {!viaMaster && (
              <RequirePermission anyOf={[p.invoices.create]}>
                <Button variant="contained" size="small" onClick={() => { openBilling('issue'); }}>
                  {t('policies.billing.actions.issue')}
                </Button>
              </RequirePermission>
            )}
            {!viaMaster && (
              <RequirePermission anyOf={[p.invoices.create]}>
                <Button variant="outlined" size="small" onClick={() => { openBilling('period'); }}>
                  {t('policies.billing.actions.period')}
                </Button>
              </RequirePermission>
            )}
            <RequirePermission anyOf={[p.invoices.correct]}>
              <Button variant="outlined" size="small" onClick={() => { openBilling('correction'); }}>
                {t('policies.billing.actions.correction')}
              </Button>
            </RequirePermission>
            <RequirePermission anyOf={[p.invoices.create]}>
              <Button variant="outlined" size="small" onClick={() => { openBilling('schedule'); }}>
                {t('policies.billing.actions.schedule')}
              </Button>
            </RequirePermission>
          </Stack>
        }
      />

      <Stack spacing={1} component="section" aria-labelledby="policy-own-invoices">
        <Typography variant="h3" component="h2" id="policy-own-invoices">
          {t('policies.billing.ownInvoices')}
        </Typography>
        {invoices.length === 0 ? (
          <EmptyState description={viaMaster ? t('policies.billing.ownEmptyViaMaster') : t('policies.billing.ownEmpty')} />
        ) : (
          <TableContainer component={Paper} variant="outlined">
            <Table size="small" aria-labelledby="policy-own-invoices">
              <TableHead>
                <TableRow>
                  <TableCell>{t('policies.billing.columns.number')}</TableCell>
                  <TableCell>{t('policies.billing.columns.type')}</TableCell>
                  <TableCell>{t('policies.billing.columns.period')}</TableCell>
                  <TableCell align="right">{t('policies.billing.columns.gross')}</TableCell>
                  <TableCell>{t('policies.billing.columns.status')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {invoices.map((invoice) => (
                  <TableRow key={invoice.code} data-testid="own-invoice">
                    <TableCell>
                      <Link component="button" type="button" sx={{ fontFamily: 'monospace' }} onClick={() => { openInvoice(invoice.code ?? ''); }}>
                        {invoice.invoiceNumber === undefined || invoice.invoiceNumber === '' ? invoice.code : invoice.invoiceNumber}
                      </Link>
                    </TableCell>
                    <TableCell>
                      <Chip size="small" label={t(`policies.billing.types.${invoice.type ?? ''}` as never, { defaultValue: invoice.type ?? '' })} />
                    </TableCell>
                    <TableCell>{period(invoice.billingIntervalFrom, invoice.billingIntervalTo)}</TableCell>
                    <TableCell align="right">{money(invoice.grossAmount, invoice.currency)}</TableCell>
                    <TableCell>
                      <StatusChip
                        status={invoice.status ?? ''}
                        label={t(`policies.billing.invoiceStatus.${invoice.status ?? ''}` as never, { defaultValue: invoice.status ?? '' })}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Stack>

      {(viaMaster || lines.length > 0) && (
        <Stack spacing={1} component="section" aria-labelledby="policy-master-lines">
          <Typography variant="h3" component="h2" id="policy-master-lines">
            {t('policies.billing.masterLines')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('policies.billing.masterLinesDescription')}
          </Typography>
          {lines.length === 0 ? (
            <EmptyState description={t('policies.billing.masterLinesEmpty')} />
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small" aria-labelledby="policy-master-lines">
                <TableHead>
                  <TableRow>
                    <TableCell>{t('policies.billing.columns.number')}</TableCell>
                    <TableCell>{t('policies.billing.columns.type')}</TableCell>
                    <TableCell>{t('policies.billing.columns.period')}</TableCell>
                    <TableCell align="right">{t('policies.billing.columns.amount')}</TableCell>
                    <TableCell>{t('policies.billing.columns.status')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {lines.map(({ invoice, item }, index) => (
                    <TableRow key={`${invoice.code ?? ''}-${String(index)}`} data-testid="master-line">
                      <TableCell>
                        <Link component="button" type="button" sx={{ fontFamily: 'monospace' }} onClick={() => { openInvoice(invoice.code ?? ''); }}>
                          {invoice.invoiceNumber === undefined || invoice.invoiceNumber === '' ? invoice.code : invoice.invoiceNumber}
                        </Link>
                      </TableCell>
                      <TableCell>
                        <Chip size="small" label={t(`policies.billing.types.${invoice.type ?? ''}` as never, { defaultValue: invoice.type ?? '' })} />
                      </TableCell>
                      <TableCell>{period(item.billingIntervalFrom, item.billingIntervalTo)}</TableCell>
                      <TableCell align="right">{money(item.grossAmount, invoice.currency)}</TableCell>
                      <TableCell>
                        <StatusChip
                          status={invoice.status ?? ''}
                          label={t(`policies.billing.invoiceStatus.${invoice.status ?? ''}` as never, { defaultValue: invoice.status ?? '' })}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Stack>
      )}
    </Stack>
  );
}
